/**
 * MONSTER TOWN — 2D MONSTER PORTRAIT. HUD/AUDIO/TOUCH CREW (src/ui/**).
 *
 * The flat version of the kid's monster, drawn from the SAME `MonsterConfig` the
 * monster crew builds the 3D avatar from. Used by the creator preview, the save
 * slot picker and the customer face on a venue order ticket, so the picture of
 * "you" is consistent everywhere in the UI.
 *
 * This is not a substitute for the 3D avatar and is not trying to be — it is the
 * portrait, it is cheap, it is DOM, and it renders identically on the oldest
 * iPad we care about. Colours all come from `palette.ts`.
 */

import { css } from '../core/palette'
import type { MonsterConfig } from '../core/types'

const INK = css('ink')
const S = `stroke="${INK}" stroke-linecap="round" stroke-linejoin="round"`
const OUT = `${S} stroke-width="4"`
const DET = `${S} stroke-width="3"`

const BODIES: Record<MonsterConfig['shape'], string> = {
  blob: 'M50 16 C80 16 92 42 90 62 C88 84 72 94 50 94 C28 94 12 84 10 62 C8 42 20 16 50 16 Z',
  tall: 'M50 8 C70 8 78 28 78 50 L78 76 C78 88 66 96 50 96 C34 96 22 88 22 76 L22 50 C22 28 30 8 50 8 Z',
  round: 'M50 12 C73 12 92 31 92 54 C92 77 73 96 50 96 C27 96 8 77 8 54 C8 31 27 12 50 12 Z',
  chunky:
    'M20 22 L80 22 C88 22 92 28 92 38 L92 76 C92 88 84 94 72 94 L28 94 C16 94 8 88 8 76 L8 38 C8 28 12 22 20 22 Z',
}

function hornsFor(cfg: MonsterConfig, accent: string): string {
  switch (cfg.horns) {
    case 'none':
      return ''
    case 'nubs':
      return `
        <path d="M32 22 L30 8" ${OUT} stroke-width="9" fill="none"/>
        <path d="M68 22 L70 8" ${OUT} stroke-width="9" fill="none"/>
        <circle cx="30" cy="7" r="7" fill="${accent}" ${DET}/>
        <circle cx="70" cy="7" r="7" fill="${accent}" ${DET}/>`
    case 'curved':
      return `
        <path d="M30 26 C14 16 16 4 30 2" stroke="${accent}" stroke-width="11" fill="none" stroke-linecap="round"/>
        <path d="M70 26 C86 16 84 4 70 2" stroke="${accent}" stroke-width="11" fill="none" stroke-linecap="round"/>
        <path d="M30 26 C14 16 16 4 30 2 M70 26 C86 16 84 4 70 2" ${DET} fill="none"/>`
    case 'antlers':
      return `
        <path d="M34 24 L28 6 L14 0 M28 12 L14 8 M66 24 L72 6 L86 0 M72 12 L86 8"
              stroke="${accent}" stroke-width="8" fill="none" stroke-linecap="round"/>`
    case 'antennae':
      return `
        <path d="M36 22 C28 10 24 6 20 2" ${OUT} fill="none"/>
        <path d="M64 22 C72 10 76 6 80 2" ${OUT} fill="none"/>
        <circle cx="19" cy="3" r="7" fill="${accent}" ${DET}/>
        <circle cx="81" cy="3" r="7" fill="${accent}" ${DET}/>`
  }
}

function eyesFor(cfg: MonsterConfig): string {
  const white = css('eyeWhite')
  const pupil = css('pupil')
  switch (cfg.eyes) {
    case 'two':
      return `
        <circle cx="37" cy="50" r="13" fill="${white}" ${OUT}/>
        <circle cx="63" cy="50" r="13" fill="${white}" ${OUT}/>
        <circle cx="40" cy="53" r="6" fill="${pupil}"/>
        <circle cx="66" cy="53" r="6" fill="${pupil}"/>
        <circle cx="36" cy="48" r="2.5" fill="${white}"/>
        <circle cx="62" cy="48" r="2.5" fill="${white}"/>`
    case 'one':
      return `
        <circle cx="50" cy="48" r="21" fill="${white}" ${OUT}/>
        <circle cx="54" cy="52" r="9" fill="${pupil}"/>
        <circle cx="49" cy="45" r="3.5" fill="${white}"/>`
    case 'three':
      return `
        <circle cx="30" cy="54" r="10" fill="${white}" ${OUT}/>
        <circle cx="50" cy="38" r="10" fill="${white}" ${OUT}/>
        <circle cx="70" cy="54" r="10" fill="${white}" ${OUT}/>
        <circle cx="32" cy="56" r="4.5" fill="${pupil}"/>
        <circle cx="52" cy="40" r="4.5" fill="${pupil}"/>
        <circle cx="72" cy="56" r="4.5" fill="${pupil}"/>`
    case 'sleepy':
      return `
        <path d="M26 50 Q37 62 48 50" ${OUT} stroke-width="6" fill="none"/>
        <path d="M52 50 Q63 62 74 50" ${OUT} stroke-width="6" fill="none"/>`
  }
}

function patternFor(cfg: MonsterConfig, accent: string, clipId: string): string {
  switch (cfg.pattern) {
    case 'plain':
      return ''
    case 'belly':
      return `<ellipse cx="50" cy="68" rx="26" ry="21" fill="${accent}" ${DET} clip-path="url(#${clipId})"/>`
    case 'spots':
      return `<g clip-path="url(#${clipId})">
        <circle cx="30" cy="40" r="9" fill="${accent}"/>
        <circle cx="70" cy="34" r="7" fill="${accent}"/>
        <circle cx="66" cy="74" r="10" fill="${accent}"/>
        <circle cx="30" cy="76" r="7" fill="${accent}"/></g>`
    case 'stripes':
      return `<g clip-path="url(#${clipId})">
        <path d="M12 66 L88 58 M12 82 L88 74 M18 50 L88 42" stroke="${accent}" stroke-width="9"
              fill="none" stroke-linecap="round"/></g>`
    case 'star':
      return `<g clip-path="url(#${clipId})">
        <path d="M50 52 L57 70 L77 72 L62 84 L66 100 L50 90 L34 100 L38 84 L23 72 L43 70 Z" fill="${accent}"/></g>`
  }
}

let uid = 0

/** SVG markup for a monster portrait. Deterministic for the same config. */
export function monsterPortraitSvg(cfg: MonsterConfig): string {
  const clipId = `mtclip${uid++}`
  const body = css(cfg.color)
  const accent = css(cfg.accentColor)
  const path = BODIES[cfg.shape]
  return `<svg viewBox="-4 -6 108 108" xmlns="http://www.w3.org/2000/svg" focusable="false" aria-hidden="true">
    <defs><clipPath id="${clipId}"><path d="${path}"/></clipPath></defs>
    ${hornsFor(cfg, accent)}
    <path d="${path}" fill="${body}" ${OUT}/>
    ${patternFor(cfg, accent, clipId)}
    <path d="${path}" fill="none" ${OUT}/>
    ${eyesFor(cfg)}
    <ellipse cx="24" cy="64" rx="7" ry="5" fill="${css('blush')}" opacity="0.75"/>
    <ellipse cx="76" cy="64" rx="7" ry="5" fill="${css('blush')}" opacity="0.75"/>
    <path d="M42 72 Q50 80 58 72" ${DET} fill="none"/>
  </svg>`
}

/**
 * A portrait as a DOM element, sized in CSS px.
 *
 * Sized inline exactly like `iconEl()` — see the long note there. A viewBox-only
 * `<svg>` inflates to its containing block the moment `.mt-ico` is not applied,
 * which is how a picture ends up covering the screen.
 */
export function monsterPortrait(cfg: MonsterConfig, sizePx: number): HTMLElement {
  const span = document.createElement('span')
  span.className = 'mt-ico'
  span.style.display = 'block'
  span.style.flex = 'none'
  span.style.width = `${sizePx}px`
  span.style.height = `${sizePx}px`
  span.innerHTML = monsterPortraitSvg(cfg)
  const svg = span.firstElementChild
  if (svg !== null) {
    svg.setAttribute('width', '100%')
    svg.setAttribute('height', '100%')
  }
  return span
}
